import React, { useEffect } from "react";
import {
  Drawer,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Link,
  VStack,
  Text,
  Flex,
  Tooltip,
  Heading,
  Box,
  Icon,
  Badge,
} from "@chakra-ui/react";
import { getKnowledgeBaseArticles } from "../../api";
import { BiLinkExternal, BiLogoYoutube } from "react-icons/bi";
import { MdHelp } from "react-icons/md";
import { BsChatDotsFill } from "react-icons/bs";
import { ToolbarIconButton } from "../../components/design_library/toolbar/ToolbarIconButton";
import { ToolbarButton } from "../../components/design_library/toolbar/ToolbarButton";
import { KnowledgeBaseHelperTopics } from "../../types";

interface Article {
  id: string;
  title: string;
  excerpt: string;
  slug: string;
}

export default function KnowledgeBaseHelper({ topic }: { topic: string }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = React.useState(true);
  const [articles, setArticles] = React.useState<Article[]>([]);
  const [isFirstTimeUser, setIsFirstTimeUser] = React.useState(false);

  const getArticles = async () => {
    const as = await getKnowledgeBaseArticles(true, topic);
    setArticles(as.articles);
    setLoading(false);
  };

  useEffect(() => {
    getArticles();
  }, []);

  useEffect(() => {
    if (!loading && articles.length > 0) {
      const visitedKey = `hasVisited_${topic}`;
      const hasVisited = localStorage.getItem(visitedKey);
      if (!hasVisited) {
        setIsFirstTimeUser(true);
        localStorage.setItem(visitedKey, "true");
      }
    }
  }, [loading, articles, topic, onOpen]);

  if (articles.length === 0) return null;

  return (
    <>
      <Box position="relative" display="inline-flex">
        {isFirstTimeUser && (
          <Box 
            position="absolute" 
            top="-2px" 
            right="-2px" 
            w="8px" 
            h="8px" 
            bg="var(--accent)" 
            borderRadius="full" 
            boxShadow="0 0 0 2px var(--bg)"
            zIndex={1}
          />
        )}
          <Box display={{ base: "block", md: "none" }}>
            <ToolbarIconButton
              aria-label="Help and guides"
              icon={<MdHelp />}
              onClick={() => {
                onOpen();
                setIsFirstTimeUser(false);
              }}
              variant="ghost"
            />
          </Box>
        <Tooltip label="Help and guides" placement="bottom" hasArrow>
          <Box display={{ base: "none", md: "block" }}>
            <ToolbarButton
              aria-label="Help and guides"
              onClick={() => {
                onOpen();
                setIsFirstTimeUser(false);
              }}
              variant="ghost"
              leftIcon={<Icon as={MdHelp} color="var(--accent)" boxSize={4} />}
            >
              Help
            </ToolbarButton>
          </Box>
        </Tooltip>
      </Box>

      <Drawer onClose={onClose} isOpen={isOpen} size="sm">
        <DrawerOverlay backdropFilter="blur(4px)" bg="rgba(0, 0, 0, 0.6)" />
        <DrawerContent 
          bg="var(--bg)" 
          boxShadow="2xl"
          borderLeft="1px solid rgba(255, 255, 255, 0.08)"
        >
          <DrawerCloseButton
            onClick={() => {
              onClose();
              setIsFirstTimeUser(false);
            }}
            size="lg"
            p={6}
            color="var(--white)"
            _hover={{ bg: "var(--dark-gray)" }}
            _focusVisible={{
              outline: "2px solid var(--accent)",
              outlineOffset: "2px",
            }}
          />
          <Flex 
            bg="var(--dark-gray)" 
            p={4} 
            alignItems="center"
            borderBottom="1px solid rgba(255, 255, 255, 0.08)"
          >
            <Icon as={MdHelp} color="var(--accent)" boxSize={6} mr={3} />
            <DrawerHeader flex={1} fontSize="xl" p={0} color="var(--white)">
              {topic} Guide
            </DrawerHeader>
          </Flex>

          <VStack spacing={6} p={6} align="stretch">
            <Box>
              <Heading size="md" mb={4} color="var(--white)">
                Articles
                <Badge 
                  ml={2} 
                  bg="var(--accent)" 
                  color="white"
                  fontSize="xs"
                  borderRadius="md"
                  px={2}
                  py={1}
                >
                  {articles.length}
                </Badge>
              </Heading>
              <VStack spacing={2} align="stretch">
                {articles
                  .sort((a, b) => a.title.localeCompare(b.title))
                  .map((article) => (
                    <Tooltip key={article.id} label={article.excerpt} placement="left" hasArrow>
                      <Link
                        p={3}
                        borderRadius="6px"
                        bg="rgba(255, 255, 255, 0.03)"
                        border="1px solid rgba(255, 255, 255, 0.08)"
                        _hover={{
                          bg: "var(--dark-gray)",
                          borderColor: "var(--accent)",
                          textDecoration: "none",
                        }}
                        _focusVisible={{
                          outline: "2px solid var(--accent)",
                          outlineOffset: "2px",
                        }}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        href={`https://wealthbee.io/knowledgebase/${article.slug}`}
                        target="_blank"
                        transition="all 0.2s"
                      >
                        <Text fontSize="14px" noOfLines={1} color="var(--white)">
                          {article.title}
                        </Text>
                        <Icon as={BiLinkExternal} ml={2} color="var(--accent)" flexShrink={0} />
                      </Link>
                    </Tooltip>
                  ))}
              </VStack>
            </Box>

            {(topic === KnowledgeBaseHelperTopics.DASHBOARD ||
              topic === KnowledgeBaseHelperTopics.TRANSACTIONS ||
              topic === KnowledgeBaseHelperTopics.POSITIONS) && (
              <Box>
                <Heading size="md" mb={4} color="var(--white)">
                  Audio Guide
                </Heading>
                <Box
                  p={4}
                  bg="rgba(255, 255, 255, 0.03)"
                  border="1px solid rgba(255, 255, 255, 0.08)"
                  borderRadius="6px"
                >
                  <audio
                    controls
                    style={{
                      width: "100%",
                      borderRadius: "6px",
                      filter: "invert(0.9)",
                    }}
                    controlsList="nodownload"
                  >
                    <source
                      src={
                        topic === KnowledgeBaseHelperTopics.DASHBOARD
                          ? "https://cdn.wealthbee.io/main/audio/dashboard.mp3"
                          : topic === KnowledgeBaseHelperTopics.POSITIONS
                          ? "https://cdn.wealthbee.io/main/audio/positions.mp3"
                          : topic === KnowledgeBaseHelperTopics.TRANSACTIONS
                          ? "https://cdn.wealthbee.io/main/audio/transactions.mp3"
                          : ""
                      }
                      type="audio/mpeg"
                    />
                    Your browser does not support the audio element.
                  </audio>
                </Box>
              </Box>
            )}

            <Box>
              <Heading size="md" mb={4} color="var(--white)">
                Additional Support
              </Heading>
              <VStack spacing={3}>
                <Link
                  w="100%"
                  href="https://www.youtube.com/@WealthBeeIo"
                  target="_blank"
                  _hover={{ textDecoration: "none" }}
                >
                  <ToolbarButton
                    w="100%"
                    leftIcon={<BiLogoYoutube size="20px" />}
                    variant="outline"
                    borderColor="red.500"
                    color="red.400"
                    _hover={{ 
                      bg: "rgba(255, 0, 0, 0.1)",
                      borderColor: "red.400"
                    }}
                  >
                    YouTube Tutorials
                  </ToolbarButton>
                </Link>

                <ToolbarButton
                  w="100%"
                  leftIcon={<BsChatDotsFill size="16px" />}
                  variant="outline"
                  borderColor="blue.500"
                  color="blue.400"
                  _hover={{ 
                    bg: "rgba(0, 100, 255, 0.1)",
                    borderColor: "blue.400"
                  }}
                  onClick={() => {
                    (window as any).$crisp.push(["do", "chat:open"]);
                  }}
                >
                  Live Support Chat
                </ToolbarButton>
              </VStack>
            </Box>
          </VStack>
        </DrawerContent>
      </Drawer>
    </>
  );
}
